import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ResolveEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: {
    id: string;
    title: string;
    yes_pool: number;
    no_pool: number;
  } | null;
  onEventResolved: () => void;
}

const ResolveEventModal = ({ isOpen, onClose, event, onEventResolved }: ResolveEventModalProps) => {
  const { locale } = useLocale();
  const [isLoading, setIsLoading] = useState(false);
  const [pendingOutcome, setPendingOutcome] = useState<boolean | null>(null);

  if (!event) return null;

  const handleSelectOutcome = (outcome: boolean) => {
    setPendingOutcome(outcome);
  };

  const handleConfirmResolve = async () => {
    if (pendingOutcome === null) return;
    
    setIsLoading(true);

    try {
      // Get current session for authorization
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error(locale.language === "pt" ? "Não autenticado" : "Not authenticated");
      }

      const { data, error } = await supabase.functions.invoke("resolve-event", {
        body: { event_id: event.id, outcome: pendingOutcome },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) throw error;

      toast.success(
        locale.language === "pt"
          ? `Evento resolvido! ${data.winners_count} vencedores`
          : `Event resolved! ${data.winners_count} winners`,
        {
          description:
            locale.language === "pt"
              ? `${data.losing_pool.toLocaleString()} CallyCoins distribuídos`
              : `${data.losing_pool.toLocaleString()} CallyCoins distributed`,
        }
      );

      onEventResolved();
      handleClose();
    } catch (error: any) {
      toast.error(error.message || "Error resolving event");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPendingOutcome(null);
    onClose();
  };

  const handleBack = () => {
    setPendingOutcome(null);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={handleClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
          >
            <div className="glass-card p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground">
                  {pendingOutcome !== null
                    ? (locale.language === "pt" ? "Confirmar Resolução" : "Confirm Resolution")
                    : (locale.language === "pt" ? "Resolver Evento" : "Resolve Event")}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-muted transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {event.title}
              </p>

              {pendingOutcome === null ? (
                <>
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 mb-6">
                    <AlertTriangle size={18} className="text-destructive" />
                    <span className="text-sm text-destructive">
                      {locale.language === "pt"
                        ? "Esta ação é irreversível!"
                        : "This action is irreversible!"}
                    </span>
                  </div>

                  <div className="mb-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      {locale.language === "pt" ? "Pools atuais:" : "Current pools:"}
                    </p>
                    <div className="flex gap-4">
                      <div className="flex-1 p-3 rounded-lg bg-success/10 text-center">
                        <p className="text-success font-bold">
                          {event.yes_pool.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {locale.language === "pt" ? "SIM" : "YES"}
                        </p>
                      </div>
                      <div className="flex-1 p-3 rounded-lg bg-destructive/10 text-center">
                        <p className="text-destructive font-bold">
                          {event.no_pool.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {locale.language === "pt" ? "NÃO" : "NO"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm font-medium text-foreground mb-3">
                    {locale.language === "pt" ? "Qual foi o resultado?" : "What was the outcome?"}
                  </p>

                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="success"
                      onClick={() => handleSelectOutcome(true)}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle size={18} />
                      {locale.language === "pt" ? "SIM Ganhou" : "YES Won"}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => handleSelectOutcome(false)}
                      className="flex items-center gap-2"
                    >
                      <XCircle size={18} />
                      {locale.language === "pt" ? "NÃO Ganhou" : "NO Won"}
                    </Button>
                  </div>

                  <Button
                    variant="ghost"
                    onClick={handleClose}
                    className="w-full mt-3"
                  >
                    {locale.language === "pt" ? "Cancelar" : "Cancel"}
                  </Button>
                </>
              ) : (
                <>
                  <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle size={20} className="text-destructive" />
                      <span className="font-semibold text-destructive">
                        {locale.language === "pt" ? "Tem certeza?" : "Are you sure?"}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {locale.language === "pt"
                        ? `Você está prestes a resolver este evento como "${pendingOutcome ? "SIM" : "NÃO"} Ganhou". Esta ação não pode ser desfeita e distribuirá os prêmios aos vencedores.`
                        : `You are about to resolve this event as "${pendingOutcome ? "YES" : "NO"} Won". This action cannot be undone and will distribute winnings to winners.`}
                    </p>
                  </div>

                  <div className="flex items-center justify-center gap-2 p-3 rounded-lg mb-6" 
                    style={{ backgroundColor: pendingOutcome ? 'hsl(var(--success) / 0.1)' : 'hsl(var(--destructive) / 0.1)' }}>
                    {pendingOutcome ? (
                      <CheckCircle size={24} className="text-success" />
                    ) : (
                      <XCircle size={24} className="text-destructive" />
                    )}
                    <span className={`font-bold text-lg ${pendingOutcome ? 'text-success' : 'text-destructive'}`}>
                      {pendingOutcome
                        ? (locale.language === "pt" ? "SIM Ganhou" : "YES Won")
                        : (locale.language === "pt" ? "NÃO Ganhou" : "NO Won")}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      disabled={isLoading}
                    >
                      {locale.language === "pt" ? "Voltar" : "Go Back"}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleConfirmResolve}
                      disabled={isLoading}
                    >
                      {isLoading
                        ? (locale.language === "pt" ? "Resolvendo..." : "Resolving...")
                        : (locale.language === "pt" ? "Confirmar" : "Confirm")}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ResolveEventModal;
